import { PRIMEIRA_PERGUNTA_DO_PRIMEIRO_CONTATO } from '@modules/aawz-intelligence-v2/constants/TextChat'
import { filaDeAnexos } from '@modules/aawz-intelligence-v2/utils/anexos-pendentes'
import { motorDeVoz } from '@modules/aawz-intelligence-v2/utils/motor-de-voz'
import { prefereMenosMovimento } from '@modules/aawz-intelligence-v2/utils/revelacao-de-texto'

// A PRIMEIRA PERGUNTA, DIGITADA pelo chat (spec-009, US-2 — irmã de
// `view-chat-boas-vindas.mixin.js`).
//
// Na primeira visita — e só nela — a frase é escrita letra a letra no composer,
// como se o assistente estivesse digitando por ela. Termina de escrever, um
// respiro curto, o BOTÃO DE ENVIAR pulsa, e a mensagem parte.
//
// ★ **A sequência inteira é a mensagem.** A digitação mostra o que perguntar; o
// pulso diz de onde aquilo vai sair — o consultor aprende o gesto vendo-o
// acontecer uma vez. Envio instantâneo ensinaria só o resultado.
//
// ★ **Zero backend.** Quem diz que é o primeiro contato é o servidor, pela
// MESMA leitura que a boas-vindas já faz no `created()` (`boasVindasDoServidor`,
// com `deve_entregar`). Este mixin só lê o veredito dela — um `if` próprio daria
// a ilusão da mesma garantia com duas abas abertas.
//
// ★ **Mesmo tom do irmão: falha de segundo plano NUNCA derruba o chat.** Todo
// desfecho ruim — servidor mudo, composer fora da tela, store de outra
// superfície — é o mesmo: não digita, não envia, não avisa ninguém.
//
// ⚠️ **Depende de `view-chat.vue`** (`messageInput`, `turnoEmVoo`,
// `chaveDaFilaDeAnexos`, `hasMessages`, `carregandoMensagens`, `focarComposer`,
// `onSendMessage`) e de `view-chat-boas-vindas.mixin.js`
// (`superficieDaBoasVindas`, `boasVindasDoServidor`). Ele não é montável
// sozinho — é uma fatia daquela view, extraída porque o `.vue` já passa de
// 2.900 linhas.

/**
 * O intervalo BASE entre uma letra e a seguinte.
 *
 * ★ Ele é só a base: o intervalo REAL varia por letra (`RITMO_DAS_LETRAS`) e
 * respira depois de espaço e de pontuação. Cadência constante lê como máquina
 * — o olho percebe o serrilhado.
 *
 * ⚠️ **ESTE NÚMERO É DE PERCEPÇÃO e ESTÁ FECHADO — resultado de TRÊS rodadas
 * ao vivo com o usuário (18/09/2026), não de opinião no código.** Ele foi
 * atravessado nos dois sentidos até parar aqui:
 *
 * | rodada | base | a frase inteira | veredito NA TELA |
 * |---|---|---|---|
 * | 1ª (escrita) | 34ms | ~0,45s | *"a digitação está MUITO rápida"* |
 * | 2ª | 140ms | ~1,8s | ainda rápido |
 * | 3ª | 280ms | ~3,6s | **lenta demais** |
 * | 4ª | 190ms | ~2,4s | ainda lenta |
 * | **fechado** | **145ms** | **~1,8s** | aceito |
 *
 * ★ O valor não é um meio-termo aritmético: é o ponto em que o usuário parou
 * de pedir mudança, com a tela na frente. As duas primeiras rodadas provaram
 * que a frase precisa ser VISTA sendo escrita; a terceira provou que existe
 * um teto — passado dele, a tela fica parada e a espera vira custo.
 *
 * ⚠️ **Mexer de novo exige teste NA TELA, não leitura de código.** A faixa
 * validada está em `MS_DA_DIGITACAO_INTEIRA_*` abaixo, com teste que mede a
 * soma real sobre a frase real — quem mudar tem de reafirmá-la por escrito.
 */
/**
 * ⚠️ **TRAVA DE TESTE — o envio automático da primeira pergunta.**
 *
 * `false` = a frase é DIGITADA no campo (com o pulso e tudo), e **para ali**:
 * quem aperta Enter é a pessoa. `true` = comportamento de produção, a mensagem
 * parte sozinha no fim do pulso.
 *
 * Existe porque testar a apresentação com o envio automático ligado é
 * impossível na prática: a cada F5 a frase parte sozinha, consome o primeiro
 * contato daquele profissional (`deve_entregar` passa a responder
 * `ja_usava_antes` assim que a conversa existe) e obriga a criar outro
 * profissional para a próxima tentativa.
 *
 * ★ **Voltar para `true` antes do PR.** Com `false`, a US-2 da spec-009 (a
 * frase que parte sozinha) não acontece — e é ela que dá sentido à ordem da
 * tela: a apresentação entra como resposta de uma pergunta que existe.
 */
export const ENVIO_AUTOMATICO_ATIVO = true

/**
 * ⚠️ **Recalibrado de 145 para 160 quando a frase virou `/onboarding`.**
 *
 * A conta é fechada: a frase tem 11 caracteres, o ciclo de `RITMO_DAS_LETRAS`
 * soma exatamente `10.0` sobre os 10 intervalos que existem, e nenhum caractere
 * dela é espaço ou pontuação — então `duracaoDaDigitacao` é simplesmente
 * `base × 10`. Com 145 dava **1450ms**, 50ms ABAIXO do piso da faixa, e o teste
 * que mede a soma real reprovava.
 *
 * 160 dá **1600ms**, com 100ms de folga do piso. Foi o que o próprio comentário
 * de `PRIMEIRA_PERGUNTA_DO_PRIMEIRO_CONTATO` mandou fazer: *"o que se ajusta é
 * `MS_BASE_POR_LETRA`, nunca a faixa"* — a faixa é o que foi validado na tela.
 *
 * ★ O número é de PERCEPÇÃO: 1600 é o menor valor com folga, não um ótimo
 * medido. Se na tela ficar rápido, os degraus da mesma conta são 175 (1750ms) e
 * 190 (1900ms, o centro da faixa e a base que a frase anterior usava).
 */
export const MS_BASE_POR_LETRA = 160

/**
 * A faixa em que a frase INTEIRA tem de caber, em ms — a promessa que o
 * usuário validou olhando.
 *
 * ★ Ela existe para que a calibragem não seja um número solto: mexer em
 * `MS_BASE_POR_LETRA`, no `RITMO_DAS_LETRAS` ou nos extras derruba o teste que
 * mede a soma real sobre a frase real, e quem mexeu tem de reafirmar a faixa
 * por escrito — que é o que o usuário aprovou.
 */
export const MS_DA_DIGITACAO_INTEIRA_MINIMO = 1500
export const MS_DA_DIGITACAO_INTEIRA_MAXIMO = 2300

/**
 * A variação por letra — cíclica e DETERMINÍSTICA, nunca `Math.random`.
 *
 * Aleatório de verdade daria o mesmo efeito e tiraria a possibilidade de
 * afirmar qualquer coisa sobre o ritmo num teste. Sete multiplicadores primos
 * entre si com o tamanho da frase bastam para nunca repetir o padrão dentro de
 * uma digitação.
 */
export const RITMO_DAS_LETRAS = Object.freeze([
  1, 0.7, 1.25, 0.85, 1.4, 0.75, 1.1,
])

/**
 * Depois de um espaço a mão hesita um pouco.
 *
 * ⚠️ Os dois extras escalam JUNTO com `MS_BASE_POR_LETRA`: 26ms sobre uma base
 * de 140 seria imperceptível, e a hesitação deixaria de existir na prática.
 */
export const MS_EXTRA_APOS_ESPACO = 82

/** Depois de pontuação, hesita bem mais — é onde a frase respira. */
export const MS_EXTRA_APOS_PONTUACAO = 352

const PONTUACAO_QUE_RESPIRA = ',.;:!?'

/**
 * O respiro entre o fim da digitação e o pulso do botão.
 *
 * ⚠️ **É o ÚNICO trecho da sequência que encolhe quando o orçamento aperta.**
 * A digitação é pedido explícito do usuário e o pulso é o AVISO de que a
 * mensagem vai sair; nenhum dos dois pode ceder. Ele foi a 220ms quando a
 * digitação estava em ~3,6s (o total passava de 5s) e voltou a 320ms quando
 * ela fechou em ~2,4s — o orçamento respirou junto.
 *
 * O piso é ~150ms: abaixo disso o pulso cola na digitação e deixa de ler como
 * fase NOVA ("vai enviar"), virando continuação do que já estava acontecendo.
 */
export const MS_PAUSA_ANTES_DO_PULSO = 320

/**
 * Quanto o botão pulsa antes de o envio sair.
 *
 * Vira `--az-pulse-duration` no próprio botão (`az-highlight-pulse`,
 * `src/design/helpers/_effects.sass`) — o mesmo mecanismo do "Minhas
 * transcrições" do CRM. O default da classe (1.2s) é para fluxo que abre modal
 * em seguida; aqui o que vem depois é o envio, então um pulso mais curto.
 */
export const MS_DO_PULSO = 900

/**
 * `prefers-reduced-motion`: sem digitação e sem pulso — a frase aparece
 * inteira e o envio sai depois desta espera.
 *
 * ★ Quem pediu menos movimento não pediu menos função: a espera continua
 * existindo para a frase ser LIDA antes de virar mensagem.
 */
export const MS_ESPERA_SEM_MOVIMENTO = 700

export const FASE_DIGITANDO = 'digitando'
export const FASE_PAUSA = 'pausa'
export const FASE_PULSO = 'pulso'

/**
 * O intervalo até a PRÓXIMA letra, depois de escrever `caractere` na posição
 * `indice`. Puro — é o que torna o ritmo afirmável em teste.
 */
export function atrasoDaLetra(indice, caractere) {
  const multiplicador = RITMO_DAS_LETRAS[indice % RITMO_DAS_LETRAS.length]
  let atraso = MS_BASE_POR_LETRA * multiplicador
  if (caractere === ' ') atraso += MS_EXTRA_APOS_ESPACO
  if (PONTUACAO_QUE_RESPIRA.indexOf(caractere) !== -1) {
    atraso += MS_EXTRA_APOS_PONTUACAO
  }
  return Math.round(atraso)
}

/**
 * Quanto a frase INTEIRA leva para ser escrita, em ms.
 *
 * A primeira letra sai sem espera (o gesto começa na hora), então a soma é dos
 * atrasos que separam as letras — o atraso do ÚLTIMO caractere nunca acontece:
 * dali a sequência vai para o respiro.
 */
export function duracaoDaDigitacao(frase) {
  const texto = frase || ''
  let total = 0
  for (let i = 0; i < texto.length - 1; i++) {
    total += atrasoDaLetra(i, texto.charAt(i))
  }
  return total
}

/**
 * ★ **O `/onboarding` vira CHIP, como qualquer comando escolhido no menu.**
 *
 * A frase é digitada letra a letra e, ao fechar, deixa de ser texto solto no
 * campo: ela assume a mesma pílula que um `/crm` ou um `/pré-reunião` ganham
 * quando a pessoa os escolhe no `/`. Sem isto o primeiro contato mostrava um
 * comando cru onde a plataforma inteira mostra um chip — o único lugar do
 * produto em que o `/` não vira ferramenta.
 *
 * ⚠️ **Ele NÃO está no catálogo do servidor, e isso é deliberado.** O catálogo
 * (`GET /v2/api/ia-crm/atalhos` → `core/atalhos/catalogo.py`) alimenta a seção
 * "Atalhos" do menu do `/`, que está SEMPRE à mão; o onboarding só existe no
 * primeiro contato e não deve ficar ofertado para sempre. Por isso a entrada é
 * do FRONT e vive aqui, no único consumidor dela.
 *
 * ⚠️ O `id` é o slug que o servidor reconhece (`core/onboarding/comando.py::
 * e_comando_de_onboarding`) — é ele que viaja em `comando: {slug}` por
 * `apiChatAtalhos.sendAtalho`, e `executarAtalho` não confere o catálogo. O
 * `label` sai da MESMA constante que a digitação escreve, para a pílula e o
 * texto digitado nunca divergirem.
 */
export const ATALHO_DO_ONBOARDING = Object.freeze({
  id: PRIMEIRA_PERGUNTA_DO_PRIMEIRO_CONTATO.replace(/^\//, ''),
  label: PRIMEIRA_PERGUNTA_DO_PRIMEIRO_CONTATO,
})

export const viewChatPrimeiraPerguntaMixin = {
  data() {
    return {
      /**
       * Já foi decidido o que fazer com a primeira pergunta NESTA montagem —
       * levada até o fim, ou desistida por um gesto da pessoa.
       *
       * ★ Ela marca a DECISÃO, não o envio: quando uma guarda barra a frase, a
       * marca cai do mesmo jeito. Desistir é definitivo — nada de fila, nada de
       * tentar de novo quando o campo esvaziar.
       */
      primeiraPerguntaResolvida: false,
      /**
       * `null` | `digitando` | `pausa` | `pulso`.
       *
       * As três fases são momentos em que a pessoa pode assumir o controle, e
       * é por isso que elas têm nome: cada guarda precisa saber se ainda há o
       * que abortar.
       */
      faseDaPrimeiraPergunta: null,
      /**
       * O pedaço da frase que NÓS já escrevemos no campo.
       *
       * ★ É a única forma honesta de responder "este `input` é nosso?". Ele é
       * atualizado ANTES de `messageInput`, então o watcher do campo encontra
       * os dois iguais quando a letra foi nossa. E é ele que autoriza a
       * limpeza: só apagamos o que escrevemos se o campo ainda for EXATAMENTE
       * isto.
       */
      prefixoDaPrimeiraPergunta: '',
    }
  },
  computed: {
    /** Digitação, respiro ou pulso — alguma fase da sequência está em curso. */
    primeiraPerguntaEmCurso() {
      return Boolean(this.faseDaPrimeiraPergunta)
    },
    digitandoPrimeiraPergunta() {
      return this.faseDaPrimeiraPergunta === FASE_DIGITANDO
    },
    /** Vira `az-highlight-pulse` no botão de enviar do composer. */
    pulsarEnvioDoComposer() {
      return this.faseDaPrimeiraPergunta === FASE_PULSO
    },
    duracaoDoPulsoDeEnvio() {
      return `${MS_DO_PULSO}ms`
    },
    /**
     * O primeiro contato ainda vai acontecer (ou está acontecendo) NESTA tela.
     *
     * ★ É o que apaga as sugestões de prompt do estado vazio. Enquanto o chat
     * está prestes a escrever o comando pela pessoa — e enquanto o escreve —,
     * oferecer atalhos de módulo convida a sair do trilho no exato momento em
     * que a tela está ensinando qual é o trilho. Mesmo argumento já escrito
     * para o `envioTravado` do `view-chat.vue`.
     *
     * ⚠️ NÃO é `primeiraPerguntaEmCurso` sozinho: a janela que importa começa
     * ANTES da primeira letra (a tela já está montada esperando a resposta do
     * servidor) e é justamente nela que as sugestões apareciam.
     */
    primeiroContatoEmAberto() {
      if (this.primeiraPerguntaEmCurso) return true
      // ⚠️ **NÃO consultar `primeiraPerguntaResolvida` aqui.** Ela marca "já
      // decidimos o que fazer com a FRASE", não "o primeiro contato acabou" —
      // e as duas coisas se separam em pelo menos dois caminhos reais:
      //
      // - com `ENVIO_AUTOMATICO_ATIVO` desligado, a frase é escrita e PARA no
      //   campo: a marca cai, a conversa segue vazia, e a lista de módulos
      //   voltava para a tela no meio do primeiro contato;
      // - quando uma guarda barra a frase (a pessoa já estava digitando, um
      //   anexo na fila), a marca também cai — e ali o primeiro contato mal
      //   começou.
      //
      // Quem responde "isto ainda é o primeiro contato?" é o predicado abaixo:
      // superfície + o servidor tendo pedido + conversa VAZIA. Assim que a
      // primeira mensagem entra, ele devolve `false` sozinho — e aí o estado
      // vazio já nem existe mais.
      return this.ehOMomentoDaPrimeiraPergunta()
    },
  },
  watch: {
    /**
     * A resposta do servidor é ASSÍNCRONA e pode chegar depois do `mounted`.
     *
     * No caminho comum a leitura que `view-chat-boas-vindas.mixin.js` dispara
     * no `created()` só volta alguns instantes depois; presumir que ela já está
     * em mãos deixaria a frase sem sair justamente na primeira visita.
     */
    boasVindasDoServidor() {
      this.avaliarPrimeiraPergunta()
    },
    /**
     * ★ A vigília do campo — a guarda que a SEQUÊNCIA trouxe.
     *
     * Enquanto escrevemos (e enquanto o respiro e o pulso correm) o campo tem
     * conteúdo nosso. Qualquer `input` que não seja o nosso (uma tecla, um
     * colar, um apagar) faz o campo divergir do prefixo, e é isso que este
     * watcher enxerga: para na hora.
     *
     * ⚠️ Não compara com a FRASE, e sim com o prefixo já escrito: comparar com
     * a frase inteira acusaria interrupção a cada letra nossa.
     */
    messageInput(novo) {
      if (!this.primeiraPerguntaEmCurso) return
      if (novo === this.prefixoDaPrimeiraPergunta) return
      this.interromperPrimeiraPergunta()
    },
  },
  mounted() {
    // O caminho raro (resposta já em mãos: cache, remontagem da view). A
    // montagem é o piso, o watcher acima é a regra.
    this.avaliarPrimeiraPergunta()
  },
  beforeDestroy() {
    this.cancelarPassoDaPrimeiraPergunta()
  },
  methods: {
    /**
     * É a hora? Se for, decide — e a decisão é final.
     *
     * ⚠️ A marca só cai quando o MOMENTO chegou. Enquanto o servidor não
     * respondeu (ou a conversa ainda está carregando), isto não é uma
     * desistência: é um "ainda não", e o watcher volta aqui quando a resposta
     * chegar.
     */
    avaliarPrimeiraPergunta() {
      if (this.primeiraPerguntaResolvida) return
      if (!this.ehOMomentoDaPrimeiraPergunta()) return

      this.primeiraPerguntaResolvida = true
      if (this.pessoaEstaNoComando()) return
      this.comecarPrimeiraPergunta()
    },
    /**
     * As quatro condições do gatilho, e todas têm de valer juntas.
     *
     * ⚠️ `carregandoMensagens` entra aqui pelo mesmo motivo que entrou no
     * `showEmptyState`: durante a carga de uma conversa a lista está vazia, e
     * sem esta condição reabrir uma conversa do histórico passaria por
     * "conversa vazia" por uma fração de segundo — que é tudo o que este
     * gatilho precisa para disparar onde não devia.
     */
    ehOMomentoDaPrimeiraPergunta() {
      if (!this.superficieDaBoasVindas) return false
      if (!this.servidorPediuOPrimeiroContato()) return false
      // ★ [19/09/2026] A URL com conversa é fato SÍNCRONO, e é ele que fecha a
      // corrida do F5 no meio do onboarding (achado do owner na tela).
      //
      // `carregandoMensagens` só vira `true` quando `selecionarConversa` roda —
      // e ela espera o `carregarConversas()` do `abrirConversaInicial`. Entre o
      // `mounted` e essa resposta existe uma janela em que a lista está vazia
      // E ninguém está carregando: se o veredito do servidor chega aí (ele é
      // assíncrono, por isso o watcher existe), as quatro condições passam e a
      // frase é digitada POR CIMA de uma conversa que já tem onboarding em
      // curso — num composer que o próprio onboarding deixa travado
      // (`envioTravado`), então ela nem sai. A pessoa recarrega e perde o fio.
      //
      // Endereço de conversa na URL significa conversa REABERTA, nunca primeiro
      // contato: o primeiro contato começa em `/home-ia` e só ganha `/c/<uuid>`
      // depois do primeiro turno.
      if (this.idDaConversaDaRota) return false
      if (this.carregandoMensagens) return false
      return !this.hasMessages
    },
    /** O veredito do servidor, lido do mesmo lugar que a boas-vindas lê. */
    servidorPediuOPrimeiroContato() {
      const leitura = this.boasVindasDoServidor
      return Boolean(leitura && leitura.deve_entregar)
    },
    /**
     * A pessoa já começou alguma coisa? Então a vez é dela.
     *
     * Quatro gestos, e qualquer um deles basta: texto no campo, anexo na fila,
     * voz em curso e turno em voo. Escrever por cima de qualquer um seria
     * desfazer algo que ela fez de propósito — e nenhum deles tem desfazer.
     *
     * ⚠️ Conferido ANTES de começar. Os três que não são o texto continuam
     * sendo reconferidos em cada fase (`outroGestoEmCurso`) — o texto sai
     * dessa releitura porque durante a sequência o campo é nosso, e quem o
     * vigia é o watcher de `messageInput`.
     */
    pessoaEstaNoComando() {
      if ((this.messageInput || '').trim()) return true
      return this.outroGestoEmCurso()
    },
    outroGestoEmCurso() {
      if (this.temAnexoNaFila()) return true
      if (this.temVozEmCurso()) return true
      return Boolean(this.turnoEmVoo)
    },
    temAnexoNaFila() {
      const fila = filaDeAnexos(this.chaveDaFilaDeAnexos)
      const arquivos = (fila && fila.arquivos) || []
      return arquivos.length > 0
    },
    /**
     * Gravando, transcrevendo ou com a sessão de voz aberta.
     *
     * ⚠️ Os três, não só `gravando`: entre o fim de uma fala e o re-arme do
     * microfone a sessão continua ATIVA com `gravando` falso
     * (`sessaoDeVozAtiva`, `utils/estado-da-voz.js`), e escrever no campo nessa
     * janela atravessaria uma conversa falada em andamento.
     */
    temVozEmCurso() {
      return Boolean(
        motorDeVoz.gravando ||
          motorDeVoz.sessaoDeVozAtiva ||
          motorDeVoz.transcrevendo
      )
    },
    /**
     * Abre a sequência: foco no campo, e então digitação (ou a frase inteira,
     * com `prefers-reduced-motion`).
     *
     * ⚠️ O composer precisa estar NA TELA. Sem ele a frase seria escrita num
     * campo que ninguém vê — e o objetivo inteiro é que ela SEJA VISTA sendo
     * escrita.
     *
     * ★ O foco vem ANTES da primeira letra, e é ele o único "cursor": o caret
     * NATIVO do campo acompanha o texto. Um traço piscante desenhado por nós
     * competiria com ele — foi construído, ficou feio e saiu.
     */
    comecarPrimeiraPergunta() {
      try {
        if (!this.composerNaTela()) return
        this.focarComposer()
        if (prefereMenosMovimento()) {
          this.entregarPrimeiraPerguntaDeUmaVez()
          return
        }
        this.faseDaPrimeiraPergunta = FASE_DIGITANDO
        this.prefixoDaPrimeiraPergunta = ''
        this.escreverProximaLetra()
      } catch (erro) {
        // Silêncio deliberado — ver o cabeçalho. Nada de `console.warn`:
        // `tests/unit/setup.js` transforma warn em falha, e isto não é um bug
        // do chat, é um extra que não aconteceu.
        this.interromperPrimeiraPergunta()
      }
    },
    /** `prefers-reduced-motion`: a frase inteira, espera curta, envio. */
    entregarPrimeiraPerguntaDeUmaVez() {
      this.faseDaPrimeiraPergunta = FASE_PAUSA
      this.prefixoDaPrimeiraPergunta = PRIMEIRA_PERGUNTA_DO_PRIMEIRO_CONTATO
      this.messageInput = PRIMEIRA_PERGUNTA_DO_PRIMEIRO_CONTATO
      this.virarChipDaPrimeiraPergunta()
      this.agendarPasso(MS_ESPERA_SEM_MOVIMENTO, this.enviarPrimeiraPergunta)
    },
    /**
     * A próxima letra — e, antes dela, a releitura dos outros três gestos.
     *
     * A sequência dura quase dois segundos: a voz pode abrir, um anexo pode
     * entrar e um turno pode começar nesse meio-tempo. O texto NÃO é
     * reconferido aqui de propósito — o campo é nosso, e quem o vigia é o
     * watcher de `messageInput`.
     */
    escreverProximaLetra() {
      if (this.faseDaPrimeiraPergunta !== FASE_DIGITANDO) return
      if (this.outroGestoEmCurso()) {
        this.interromperPrimeiraPergunta()
        return
      }

      const indice = this.prefixoDaPrimeiraPergunta.length
      const escrito = PRIMEIRA_PERGUNTA_DO_PRIMEIRO_CONTATO.slice(0, indice + 1)
      // ⚠️ O prefixo ANTES do campo: é o que faz o watcher encontrar os dois
      // iguais e reconhecer a letra como nossa.
      this.prefixoDaPrimeiraPergunta = escrito
      this.messageInput = escrito
      this.porCursorNoFimDoCampo()

      if (escrito === PRIMEIRA_PERGUNTA_DO_PRIMEIRO_CONTATO) {
        this.fecharDigitacaoDaPrimeiraPergunta()
        return
      }
      this.agendarPasso(
        atrasoDaLetra(indice, escrito.charAt(indice)),
        this.escreverProximaLetra
      )
    },
    /** A última letra saiu: a frase vira chip e o respiro começa. */
    fecharDigitacaoDaPrimeiraPergunta() {
      this.faseDaPrimeiraPergunta = FASE_PAUSA
      this.virarChipDaPrimeiraPergunta()
      this.agendarPasso(MS_PAUSA_ANTES_DO_PULSO, this.pulsarBotaoDeEnviar)
    },
    /** O respiro acabou: o botão pulsa, e só depois a mensagem sai. */
    pulsarBotaoDeEnviar() {
      if (this.faseDaPrimeiraPergunta !== FASE_PAUSA) return
      if (this.outroGestoEmCurso()) {
        this.interromperPrimeiraPergunta()
        return
      }
      this.faseDaPrimeiraPergunta = FASE_PULSO
      this.agendarPasso(MS_DO_PULSO, this.enviarPrimeiraPergunta)
    },
    /**
     * O envio, pelo MESMO caminho do composer.
     *
     * ★ `onSendMessage` é literalmente o que o `@send` do `SendMessage`
     * dispara — nada de rota paralela. É ele quem decide entre o turno de
     * sempre e o turno falado da conversa com o avatar, e quem abrir uma
     * segunda porta aqui perde essa decisão sem perceber.
     *
     * ⚠️ Última conferência antes de mandar: o pulso é mais uma janela em que
     * ela pode ter começado a agir. Se o campo não for mais exatamente a nossa
     * frase, ela digitou — e o que está lá é dela: não enviamos e não apagamos.
     */
    enviarPrimeiraPergunta() {
      try {
        this.faseDaPrimeiraPergunta = null
        if (!this.composerNaTela()) return
        if (!this.chipDaPrimeiraPerguntaEstaPosto()) return
        if (this.outroGestoEmCurso()) return this.limparPrimeiraPergunta()
        // ⚠️ TRAVA DE TESTE — ver `ENVIO_AUTOMATICO_ATIVO` no topo do arquivo.
        // Desligada, a frase fica ESCRITA no campo e quem aperta Enter é a
        // pessoa. Tudo o que vem antes (digitação, pausa, pulso) continua
        // acontecendo: é justamente isso que se quer ver na tela ao testar.
        if (!ENVIO_AUTOMATICO_ATIVO) return
        this.despacharChipDaPrimeiraPergunta()
      } catch (erro) {
        // Idem: não envia, não avisa. A frase sai do campo para não ficar lá
        // como um rascunho que a pessoa não escreveu.
        this.limparPrimeiraPergunta()
      }
    },
    /**
     * Paramos no meio — e aqui o cuidado é com o que se APAGA.
     *
     * ★ **Só removemos o nosso pedaço se o campo ainda for EXATAMENTE ele.**
     * Divergiu (ela digitou no meio, colou, apagou)? Não se toca em nada. Texto
     * do usuário corrompido por causa de um enfeite é o pior desfecho possível
     * aqui — pior do que a feature não existir.
     */
    interromperPrimeiraPergunta() {
      this.cancelarPassoDaPrimeiraPergunta()
      this.faseDaPrimeiraPergunta = null
      this.limparPrimeiraPergunta()
    },
    /**
     * Tira do campo o que NÓS pusemos — nunca texto de terceiros.
     *
     * Duas formas, porque a frase muda de forma no meio do caminho: antes do
     * fecho ela é TEXTO (o prefixo), depois é o CHIP.
     */
    limparPrimeiraPergunta() {
      if (this.messageInput === this.prefixoDaPrimeiraPergunta) {
        this.messageInput = ''
      }
      this.tirarChipDaPrimeiraPergunta()
    },
    /**
     * O composer montado — herói (tela vazia) ou rodapé. Um dos dois, nunca
     * os dois: `view-chat.vue` os alterna por `v-if`/`v-else`.
     */
    composerDaPrimeiraPergunta() {
      return this.$refs.composerHeroi || this.$refs.composerRodape || null
    },
    /**
     * A frase digitada vira a pílula do comando.
     *
     * ⚠️ **Zerar o prefixo é o que impede a sequência de se abortar sozinha.**
     * `preencherAtalho` emite `input` com o resto digitado (aqui, `''`), e o
     * watcher de `messageInput` interrompe tudo quando o campo diverge do
     * prefixo. Sem esta linha o chip mata a própria sequência — medido por
     * mutação: removê-la deixa 3 testes vermelhos.
     *
     * ⚠️ A ORDEM das duas linhas, essa NÃO importa, e o comentário aqui já
     * chegou a afirmar que sim: watcher de Vue 2 é assíncrono, então as duas
     * acontecem no mesmo tick e o watcher só lê o resultado. Invertê-las não
     * quebra teste nenhum — conferido. O que importa é que as duas estejam no
     * mesmo tick.
     *
     * Falhar aqui não pode derrubar a sequência: sem o chip, o campo fica com
     * a frase escrita e o envio segue pelo texto, como antes.
     */
    virarChipDaPrimeiraPergunta() {
      try {
        const composer = this.composerDaPrimeiraPergunta()
        if (!composer || typeof composer.preencherAtalho !== 'function') return
        this.prefixoDaPrimeiraPergunta = ''
        composer.preencherAtalho(ATALHO_DO_ONBOARDING)
      } catch (erro) {
        // Acabamento: um chip que não foi não vira aviso nem derruba a tela.
      }
    },
    /** O chip ainda é o NOSSO, e a caixa continua sem texto de ninguém. */
    chipDaPrimeiraPerguntaEstaPosto() {
      const composer = this.composerDaPrimeiraPergunta()
      if (!composer) return false
      // Sem o chip (falha ao pô-lo), o texto digitado continua valendo.
      if (composer.atalhoSelecionado !== ATALHO_DO_ONBOARDING) {
        return this.messageInput === PRIMEIRA_PERGUNTA_DO_PRIMEIRO_CONTATO
      }
      return !String(this.messageInput || '').trim()
    },
    /**
     * O despacho — pela porta do próprio composer.
     *
     * ★ Com o chip posto, o turno do onboarding sai por `executar-atalho` →
     * `executarAtalho()` → `apiChatAtalhos.sendAtalho()`, que fala com `/chat`
     * mandando `comando: {slug}` e `query: null`. É o MESMO caminho de
     * qualquer comando escolhido no menu, e é o que o servidor reconhece em
     * `core/onboarding/comando.py`.
     *
     * Sem o chip (falha ao pô-lo), cai no envio de texto de sempre — a
     * degradação nunca deixa o primeiro contato sem acontecer.
     */
    despacharChipDaPrimeiraPergunta() {
      const composer = this.composerDaPrimeiraPergunta()
      if (composer && composer.atalhoSelecionado === ATALHO_DO_ONBOARDING) {
        composer.dispararAtalho(ATALHO_DO_ONBOARDING)
        return
      }
      this.onSendMessage(PRIMEIRA_PERGUNTA_DO_PRIMEIRO_CONTATO)
    },
    /** Desistimos: o chip sai junto, e só se ele for o nosso. */
    tirarChipDaPrimeiraPergunta() {
      const composer = this.composerDaPrimeiraPergunta()
      if (!composer) return
      if (composer.atalhoSelecionado !== ATALHO_DO_ONBOARDING) return
      if (typeof composer.limparAtalhoSelecionado !== 'function') return
      composer.limparAtalhoSelecionado()
    },
    /**
     * O caret NATIVO do campo é o único cursor desta sequência.
     *
     * Um `focus()` sobre valor escrito por programa não garante onde ele pousa,
     * e pousar no começo faria a pessoa digitar ANTES da frase.
     */
    porCursorNoFimDoCampo() {
      try {
        const composer = this.$refs.composerHeroi || this.$refs.composerRodape
        if (!composer || typeof composer.elementoDoCampo !== 'function') return
        const campo = composer.elementoDoCampo()
        if (!campo || typeof campo.setSelectionRange !== 'function') return
        const fim = (campo.value || '').length
        campo.setSelectionRange(fim, fim)
      } catch (erro) {
        // O cursor é acabamento: um `setSelectionRange` que não vai não pode
        // derrubar a tela nem virar aviso.
      }
    },
    composerNaTela() {
      return Boolean(this.$refs.composerHeroi || this.$refs.composerRodape)
    },
    agendarPasso(ms, passo) {
      this.cancelarPassoDaPrimeiraPergunta()
      this._passoDaPrimeiraPergunta = setTimeout(passo, ms)
    },
    cancelarPassoDaPrimeiraPergunta() {
      if (!this._passoDaPrimeiraPergunta) return
      clearTimeout(this._passoDaPrimeiraPergunta)
      this._passoDaPrimeiraPergunta = null
    },
  },
}
